import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Typography, Grid } from '@mui/material';
import { Email as EmailIcon } from '@mui/icons-material';

import {
  FBoxContainer,
  FHButton,
  FHAutocomplete,
  FHInputField,
  FProgressBar,
  FHPageLink,
} from '../../components/base';
import PageContainer from '../../components/layout/PageContainer';
import { clientActions, providerActions, providerAssignmentActions } from '../../store';
import { history } from '../../helpers';
import { Themes, ClinicalRelationships, EMAIL_REGEX } from '../../constants';

import type { RootState } from '../../store';
import type { ChangeEvent } from 'react';
import type { IClient, IOption, IProviderAssignment } from '../../utils/types';

interface IsendAssesmentSurveyPayload {
  client_id?: string | number;
  overriding_email?: string;
  bt_provider_id?: string | number;
}

function AssessmentSurvey() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { list: clients = [], loading: clientLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const { loading: sendingAssessmentSurvey } = useSelector((state: RootState) => state.providers);
  const { list: providerAssignments = [], loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );

  const [selectedClient, setSelectedClient] = useState<IOption | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<IOption | null>(null);
  const [overridingEmail, setOverridingEmail] = useState<string>('');
  const [invalidEmailError, setInvalidEmailError] = useState<boolean>(false);

  useEffect(() => {
    dispatch(clientActions.getList({ getAccessTokenSilently }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedClient?.id) return;

    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        client_id: selectedClient.id,
        clinical_relationship: ClinicalRelationships.Prbt,
      }),
    );
    setSelectedProvider(null);
    setOverridingEmail('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  const handleSubmit = async () => {
    if (
      !selectedClient ||
      !(selectedProvider || overridingEmail) ||
      (overridingEmail && invalidEmailError)
    )
      return;
    const data: IsendAssesmentSurveyPayload = {
      client_id: selectedClient?.id,
      overriding_email: overridingEmail,
      bt_provider_id: selectedProvider?.id,
    };

    if (!selectedProvider) {
      delete data.bt_provider_id;
    }
    if (!overridingEmail) {
      delete data.overriding_email;
    }

    const { error } = await dispatch(
      providerActions.createProviderAssessmentSurvey({
        data,
        getAccessTokenSilently,
      }),
    );

    if (!error) {
      setSelectedClient(null);
      setSelectedProvider(null);
      setOverridingEmail('');
      setInvalidEmailError(false);
      history.navigate('/admin');
    }
  };

  const handleClientChange = (_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
    if (newValue) {
      setSelectedClient(newValue);
    } else {
      setSelectedClient(null);
      setSelectedProvider(null);
    }
  };

  const handleProviderChange = (_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
    if (newValue) {
      setSelectedProvider(newValue);
      setOverridingEmail('');
      setInvalidEmailError(false);
    } else {
      setSelectedProvider(null);
    }
  };

  return (
    <PageContainer>
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        {(clientLoading || providerAssignmentLoading || sendingAssessmentSurvey) && (
          <FProgressBar />
        )}
        <FBoxContainer sx={{ borderRadius: '16px', boxShadow: 'none' }}>
          <Grid container>
            <Grid item xs={12} md={8} lg={7}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Typography textAlign="left" variant="h4">
                    Send Assessment Survey
                  </Typography>
                </Box>
                <Box
                  sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }}
                />
                <Grid container p={1} spacing={1} direction="row" alignItems="center">
                  <Grid item xs={3}>
                    <Typography textAlign={'left'}>Choose Client:</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <FHAutocomplete
                      inputHeight={40}
                      data-testid="assessment-survey-client"
                      sx={{ width: '100%', boxShadow: 'none' }}
                      placeholder="Select Client"
                      disabled={sendingAssessmentSurvey}
                      isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                      options={clients.map((client: IClient) => ({
                        id: client.id,
                        label: `${client.first_name} ${client.last_name}`,
                      }))}
                      renderOption={(props: any, option: IOption) => {
                        return (
                          <li {...props} key={option.id}>
                            {option.label}
                          </li>
                        );
                      }}
                      value={selectedClient ?? ''}
                      onChange={handleClientChange}
                    />
                  </Grid>
                </Grid>
                {selectedClient &&
                  !providerAssignmentLoading &&
                  providerAssignments?.length > 0 && (
                    <Grid container p={1} spacing={1} direction="row" alignItems="center">
                      <Grid item xs={3}>
                        <Typography textAlign={'left'}>Choose Provider:</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <FHAutocomplete
                          inputHeight={40}
                          data-testid="assessment-survey-providers"
                          sx={{ width: '100%', boxShadow: 'none' }}
                          placeholder="Select Provider"
                          disabled={sendingAssessmentSurvey || !selectedClient}
                          isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                          options={providerAssignments?.map(
                            (curProviderAssignment: IProviderAssignment) => ({
                              id: curProviderAssignment?.provider?.id,
                              label: `${curProviderAssignment?.provider?.first_name} ${curProviderAssignment?.provider?.last_name} (${curProviderAssignment?.clinical_relationship})`,
                            }),
                          )}
                          renderOption={(props: any, option: IOption) => {
                            return (
                              <li {...props} key={option.id}>
                                {option.label}
                              </li>
                            );
                          }}
                          value={selectedProvider ?? ''}
                          inputValue={selectedProvider?.label ?? ''}
                          onChange={handleProviderChange}
                        />
                      </Grid>
                    </Grid>
                  )}
                {selectedClient &&
                  !providerAssignmentLoading &&
                  providerAssignments?.length === 0 && (
                    <Grid container p={1} spacing={1} direction="row" alignItems="center">
                      <Grid item xs={3}>
                        <Typography textAlign={'left'}>Email:</Typography>
                      </Grid>
                      <Grid item xs={9}>
                        <FHInputField
                          inputHeight={40}
                          data-testid="assessment-survey-email"
                          fullWidth
                          sx={{ boxShadow: 'none' }}
                          colorTheme={Themes.Primary}
                          placeholder="Enter email"
                          disabled={sendingAssessmentSurvey || !selectedClient}
                          value={overridingEmail ?? ''}
                          onChange={(e) => {
                            const isEmailInvalid = !EMAIL_REGEX.test(e.target.value);
                            setOverridingEmail(e.target.value);
                            setSelectedProvider(null);
                            setInvalidEmailError(isEmailInvalid);
                          }}
                          error={Boolean(overridingEmail) && invalidEmailError}
                          helperText={
                            Boolean(overridingEmail) &&
                            invalidEmailError &&
                            'Please enter a valid email'
                          }
                        />
                      </Grid>
                    </Grid>
                  )}
              </Box>

              <Box ml={1} mt={3} sx={{ display: 'flex' }}>
                <FHButton
                  onClick={handleSubmit}
                  startIcon={<EmailIcon />}
                  disabled={
                    sendingAssessmentSurvey ||
                    !selectedClient ||
                    !(selectedProvider || overridingEmail) ||
                    invalidEmailError
                  }
                  data-testid="assessment-survey-button"
                >
                  Send
                </FHButton>
              </Box>
            </Grid>
          </Grid>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
}

export default AssessmentSurvey;
